import { getCandidateTxns } from '@api/form-verification';
import Navigation from '@components/navigation';
import Spinner from '@components/spinner';
import { CandidateTxn } from '@containers/home/types';
import Notifications from '@containers/notifications';
import { DATA_TEST_IDS } from '@data-test-ids';
import { TxnState } from '@global/constants/verification';
import { isClientCareerMosaic } from '@global/helpers/general';
import { useIsDesktop } from '@global/hooks';
import { Clear } from '@mui/icons-material';
import { Card, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { updateMiscStore } from '@redux/slices/misc';
import { RootState, store } from '@redux/store';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';
import LayoutContext from './context';
import TopBar from './top-bar';

const getPaddingForRoute = (pathname: string): number => {
  const zeroMarginRoutes = ['/ekyc/', '/evaluation-partner-form'];
  return zeroMarginRoutes.some((route) => pathname.includes(route) || pathname === route) ? 0 : 1;
};

export default function Layout() {
  const isDesktop = useIsDesktop();
  const location = useLocation();
  const openDemo = useSelector((s: RootState) => s.misc.openDemo);

  const [showContactUs, setShowContactUs] = useState(false);

  useEffect(() => {
    const handleGetTxns = async () => {
      const res = await getCandidateTxns(0, 100);
      if (res?.code !== 0) {
        return;
      }
      const foundItem = res?.data?.find((row: CandidateTxn) =>
        [
          TxnState.IN_PROGRESS,
          TxnState.FAILED,
          TxnState.COMPLETED,
          TxnState.INSUFFICIENT_DATA,
          TxnState.CLOSED
        ].includes(row?.state)
      );
      if (foundItem) {
        setShowContactUs(true);
      }
    };
    if (location.pathname === '/') {
      handleGetTxns();
    }
  }, [location.pathname]);

  return (
    <LayoutContext.Provider value={{ showContactUs }}>
      {!isClientCareerMosaic() && <TopBar />}
      <Grid
        container
        direction="row"
        sx={{
          margin: 'auto',
          minHeight: 0,
          flex: 1,
          bgcolor: '#F6F9FC',
          width: {
            xs: '100%',
            ...(isClientCareerMosaic() ? { sm: '80%' } : {}),
            ...(isClientCareerMosaic() ? { md: '50%' } : {}),
            lg: isClientCareerMosaic() ? '40%' : '80%'
          },
          ...(isClientCareerMosaic() ? { pt: 1 } : {})
          // height: '96vh' // Ensure full viewport height for the container
          // overflow: 'hidden' // Prevent any overflow in the main grid
        }}>
        {!isClientCareerMosaic() && isDesktop ? (
          <Grid item sx={{ width: '20vw', height: '100%' }}>
            <Navigation />
          </Grid>
        ) : null}

        {/* Middle Section with Scrollable Content */}
        <Grid
          item
          xs
          sx={{
            overflowY: 'auto', // Enables vertical scrolling
            height: '100%',
            minHeight: 0,
            flex: 1,
            p: getPaddingForRoute(location.pathname)
            // borderRadius: '16px'
            // position: 'relative'
          }}>
          {/* Suspense boundary added here so lazy-loaded routes don't unmount the Layout component (Sidebar/TopBar) */}
          <React.Suspense fallback={<Spinner />}>
            <Outlet />
          </React.Suspense>
          {import.meta.env.VITE_DEMO_VIDEO_LINK ? (
            <Dialog
              maxWidth="md"
              fullWidth
              open={openDemo}
              onClose={() => {
                store.dispatch(updateMiscStore({ openDemo: false }));
              }}
              sx={{ padding: '1em' }}>
              <DialogTitle>
                <Typography variant="body1">Watch Demo</Typography>
                <IconButton
                  sx={{ position: 'absolute', top: '0px', right: '0.5em' }}
                  onClick={() => {
                    store.dispatch(updateMiscStore({ openDemo: false }));
                  }}>
                  <Clear />
                </IconButton>
              </DialogTitle>

              <DialogContent sx={{ padding: '1em', width: '100%' }}>
                <video style={{ width: '100%', margin: 'auto' }} controls controlsList="nodownload">
                  <source
                    src={import.meta.env.VITE_DEMO_VIDEO_LINK}
                    type="video/mp4"
                    data-test-id={DATA_TEST_IDS.DEMO_VIDEO_SOURCE}
                  />
                  Your browser does not support HTML video.
                </video>
              </DialogContent>
            </Dialog>
          ) : null}
        </Grid>

        {!isClientCareerMosaic() && isDesktop && !location.pathname.includes('notifications') ? (
          <Grid item sx={{ width: '20vw', height: '100%' }}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <Notifications hideHeading />
            </Card>
          </Grid>
        ) : null}
      </Grid>
    </LayoutContext.Provider>
  );
}
